import 'vis-timeline/dist/vis-timeline-graph2d.min.css';

import { Box, For, Portal, Text } from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { DateType, TimelineOptions } from 'vis-timeline';
import { Timeline } from 'vis-timeline/standalone';
import { DataGroupCollectionType, DataItemCollectionType, TimelineEventPropertiesResult } from 'vis-timeline/types';

import { NotificationType } from '@/types/data/data.types';
import { CustomDataItem, ReplayEvent } from '@/types/replay/replay.types';

import TimelineEvent from './TimelineEvent';
import TimelineNotification from './TimelineNotification';

function filterByRange<T extends Record<K, string | number | Date>, K extends keyof T>(
  items: T[],
  startKey: K,
  options: TimelineOptions,
): T[] {
  const start = new Date(options.start ?? 0).getTime();
  const end = new Date(options.end ?? 0).getTime();

  return items.filter((item) => {
    const time = new Date(item[startKey]).getTime();
    return time >= start && time <= end;
  });
}

interface CustomTimelineProps {
  className?: string;
  events: ReplayEvent[];
  notifications: NotificationType[];
  customItems?: CustomDataItem[];
  groups?: DataGroupCollectionType;
  options?: TimelineOptions;
  currentTime: DateType;
  onclick: (currentTime: Date) => void;
}

export function CustomTimeline({
  className,
  events,
  notifications,
  customItems = [],
  groups = [],
  options = {},
  currentTime,
  onclick,
}: Readonly<CustomTimelineProps>) {
  const { formatMessage } = useIntl();

  const timelineRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const onClickRef = useRef<((properties: TimelineEventPropertiesResult) => void) | undefined>(undefined);
  const [tooltip, setTooltip] = useState<{ positionX: number; positionY: number; className: string } | null>(null);
  const onItemOver = useRef<((properties: TimelineEventPropertiesResult) => void) | undefined>(undefined);
  const timelineStart = new Date(options.start ?? 0).getTime();
  const timelineEnd = new Date(options.end ?? 0).getTime();

  const items: DataItemCollectionType = customItems.map((item, index) => ({
    id: index,
    group: item.group,
    content: item.content ?? '<div></div>',
    start: item.start,
    end: item.end,
    className: item.className,
    style: item.style ?? '',
    type: item.type,
  }));

  const filteredEvents = filterByRange(events, 'startDate', options);
  const filteredNotifications = filterByRange(notifications, 'startTime', options);

  useLayoutEffect(() => {
    const element = timelineRef.current;
    if (!element) return;

    setContainerWidth(element.clientWidth);

    const resizeObserver = new ResizeObserver(() => {
      setContainerWidth(element.clientWidth);
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!timelineRef.current) {
      return;
    }
    const newTimeline = new Timeline(timelineRef.current, items, groups, options);
    options.start && options.end && newTimeline.setWindow(new Date(options.start), new Date(options.end));
    newTimeline.addCustomTime(new Date(currentTime), 1);

    onClickRef.current = (properties: TimelineEventPropertiesResult): void => {
      onclick(properties.time);
    };
    newTimeline.on('mouseUp', onClickRef.current);

    newTimeline.on('itemout', () => {
      setTooltip(null);
    });

    setTimeline(newTimeline);

    const handleMouseOut = (event: MouseEvent) => {
      if (
        timelineRef.current &&
        event.currentTarget instanceof Node &&
        !timelineRef.current.contains(event.currentTarget)
      ) {
        setTooltip(null);
      }
    };
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      newTimeline.destroy();
      document.removeEventListener('mouseout', handleMouseOut);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (timelineRef.current && timeline) {
      options.start && options.end && timeline.setWindow(new Date(options.start), new Date(options.end));
      timeline.setCustomTime(new Date(currentTime), 1);
      options.timeAxis && timeline.setOptions({ timeAxis: options.timeAxis });
      timeline.setItems(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, options.end, options.start, options.timeAxis, items]);

  useEffect(() => {
    if (timelineRef.current && timeline) {
      timeline.off('mouseUp', onClickRef.current);
      onClickRef.current = (properties: TimelineEventPropertiesResult): void => {
        const event = timeline.getEventProperties(properties.event);
        onclick(event.time);
      };
      timeline.on('mouseUp', onClickRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onclick]);

  useEffect(() => {
    if (timelineRef.current && timeline) {
      timeline.off('itemover', onItemOver.current);
      onItemOver.current = (properties: TimelineEventPropertiesResult): void => {
        const event = timeline.getEventProperties(properties.event);
        const className = items.find((item) => item.id === event.item)?.className;

        if (className) {
          setTooltip({
            positionX: event.pageX,
            positionY: event.pageY,
            className,
          });
        }
      };
      timeline.on('itemover', onItemOver.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <Box ref={timelineRef} className={className} width="100%" style={{ containerType: 'size' }}>
      <For each={filteredEvents}>
        {(event) => (
          <TimelineEvent
            event={event}
            key={event.id}
            timelineStart={timelineStart}
            timelineEnd={timelineEnd}
            containerWidth={containerWidth}
          />
        )}
      </For>
      <For each={filteredNotifications}>
        {(notification) => (
          <TimelineNotification
            key={notification.id}
            notification={notification}
            timelineStart={timelineStart}
            timelineEnd={timelineEnd}
            containerWidth={containerWidth}
          />
        )}
      </For>
      {tooltip && (
        <Portal>
          <Text
            position="fixed"
            top={tooltip.positionY}
            left={tooltip.positionX + 20}
            padding={1}
            backgroundColor="neutral.900"
            zIndex={2}
            pointerEvents="none"
          >
            {formatMessage({ id: `replay.timeline.dataState.${tooltip.className}` })}
          </Text>
        </Portal>
      )}
    </Box>
  );
}
